import React from 'react';
import { ContentfulCoupon } from '@jbhi-fi/apis/src/contentful/types';
import { AssetTransformer } from './AssetTransformer';
import { CouponContent } from '../../types';
import { RichText } from '../../RichText';
import { getFriendlyFormattedDate } from '@jbhi-fi/utils/src/date/getFriendlyFormattedDate';
import { Block, Document, Inline } from '@contentful/rich-text-types';
import { Megaphone, Price } from '@jbhi-fi/lanyard-ui/components/Icons';

type Text = {
  nodeType: 'text';
  value: string | JSX.Element;
  marks: Array<{ type: string }>;
  data: Record<string, any>;
};

export abstract class CouponContentTransformer {
  static readonly ICON_MAP: Record<string, JSX.Element> = {
    '{{productExclusions}}': <Price width={20} />,
    '{{thingToKnow}}': <Megaphone width={20} />,
    '{{pricingInformation}}': <Price width={20} />,
  };
  static readonly regex = /(\{\{productExclusions\}\}|\{\{thingToKnow\}\}|\{\{pricingInformation\}\})/g;
  /**
   * Transforms a Contentful Document by replacing placeholders with icon text nodes.
   * @param doc Contentful Document
   * @returns Transformed Document
   */
  static termsAndConditionContentTransformer(doc: Document): Document {
    if (!doc?.content) return doc;

    type RichTextNode = Block | Inline | Text;

    const traverse = (node: RichTextNode): RichTextNode | Array<RichTextNode> | Document => {
      if (node.nodeType === 'text' && typeof node.value === 'string') {
        const parts = node.value.split(CouponContentTransformer.regex);

        if (parts.length === 1) return node;

        return parts.map((part: string) =>
          CouponContentTransformer.ICON_MAP[part]
            ? { nodeType: 'text', value: CouponContentTransformer.ICON_MAP[part], marks: [], data: {} }
            : { nodeType: 'text', value: part, marks: [], data: {} }
        );
      }

      if ('content' in node && Array.isArray(node.content)) {
        const newContent = node.content.flatMap(child => traverse(child as RichTextNode));
        return { ...node, content: newContent } as RichTextNode;
      }

      return node;
    };

    return traverse(doc) as Document;
  }

  static toCouponContent = (
    displayName: string,
    entry: ContentfulCoupon,
    expiryDate: string
  ): CouponContent | undefined => {
    if (!entry?.fields) return undefined;
    if (!entry.fields?.contentOne) return undefined;

    const transformedTerms = CouponContentTransformer.termsAndConditionContentTransformer(entry.fields.contentOne);

    return {
      displayName,
      termsLabel: entry.fields?.headingOne,
      actionsLabel: entry.fields?.headingTwo,
      barcodeLabel: entry.fields?.headingThree,
      termsAndConditions: <RichText document={transformedTerms} />,
      redemptionInstructions: entry.fields?.contentTwo ? <RichText document={entry.fields?.contentTwo} /> : undefined,
      image: AssetTransformer.toImageProps(entry.fields?.heroImage),
      additonalInstructions: entry.fields?.contentThree ? (
        <RichText document={entry.fields?.contentThree} />
      ) : undefined,
      expiryDateFormatted: getFriendlyFormattedDate(expiryDate, 'extraLong', true),
    };
  };
}
